import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function MFLoanPage() {
  // Home Loan Calculator States
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(9);
  const [loanTenure, setLoanTenure] = useState(20);

  // Calculate EMI
  const calculateEMI = () => {
    const P = loanAmount;
    const r = interestRate / 12 / 100; // Monthly interest rate
    const n = loanTenure * 12; // Total number of months
    
    if (r === 0) {
      return P / n;
    }
    
    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return emi;
  };

  const emi = calculateEMI();
  const totalAmount = emi * loanTenure * 12;
  const totalInterest = totalAmount - loanAmount;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-indigo-600">Loan Services</span>
            </h1>
            <p className="text-xl text-gray-600">
              Get the right loan solution for your financial needs
            </p>
          </div>

          {/* Content */}
          <div className="space-y-12">
            
            {/* 1. Loan Against Mutual Funds */}
            <section id="loan-against-mf" className="scroll-mt-20">
              <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-2xl p-8 mb-6">
                <h2 className="text-3xl font-bold mb-2">1. Loan Against Mutual Funds</h2>
                <p className="text-cyan-100 text-lg">Unlock liquidity without selling your investments</p>
              </div>

              <div className="space-y-6">
                {/* What is it */}
                <div className="bg-cyan-50 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">What is Loan Against Mutual Funds?</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    A loan against mutual funds allows you to borrow money by pledging your mutual fund holdings 
                    as collateral. This helps you meet urgent financial needs without selling your investments 
                    and losing out on potential future gains.
                  </p>
                </div>

                {/* Key Features */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="border-2 border-cyan-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-cyan-700 mb-3">📊 Loan Amount</h4>
                    <p className="text-gray-700">Up to 50-75% of your mutual fund portfolio value</p>
                  </div>
                  <div className="border-2 border-cyan-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-cyan-700 mb-3">💰 Interest Rate</h4>
                    <p className="text-gray-700">9-12% p.a. (Lower than personal loans)</p>
                  </div>
                  <div className="border-2 border-cyan-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-cyan-700 mb-3">⏱️ Processing Time</h4>
                    <p className="text-gray-700">24-48 hours disbursement</p>
                  </div>
                  <div className="border-2 border-cyan-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-cyan-700 mb-3">📋 Documentation</h4>
                    <p className="text-gray-700">Minimal - ID, Address proof, Bank statement</p>
                  </div>
                </div>

                {/* Benefits */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h4 className="font-bold text-lg mb-4">✅ Key Benefits</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Keep your investments growing while accessing funds</li>
                    <li>• No exit load or capital gains tax</li>
                    <li>• Flexible repayment options</li>
                    <li>• Continue earning returns on your mutual funds</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 2. Home Loan */}
            <section id="home-loan" className="scroll-mt-20">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-2xl p-8 mb-6">
                <h2 className="text-3xl font-bold mb-2">2. Home Loan</h2>
                <p className="text-green-100 text-lg">Make your dream home a reality with affordable financing</p>
              </div>

              <div className="space-y-6">
                {/* What is it */}
                <div className="bg-green-50 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">What is a Home Loan?</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    A home loan is a secured loan provided by banks and financial institutions to help you purchase, 
                    construct, or renovate residential property. The property itself serves as collateral for the loan, 
                    making it one of the most affordable borrowing options.
                  </p>
                </div>

                {/* Key Features */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="border-2 border-green-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-green-700 mb-3">🏠 Loan Amount</h4>
                    <p className="text-gray-700">Up to 90% of property value (Varies by lender)</p>
                  </div>
                  <div className="border-2 border-green-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-green-700 mb-3">💰 Interest Rate</h4>
                    <p className="text-gray-700">8.5-10% p.a. (Fixed/Floating options)</p>
                  </div>
                  <div className="border-2 border-green-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-green-700 mb-3">📅 Loan Tenure</h4>
                    <p className="text-gray-700">Up to 30 years</p>
                  </div>
                  <div className="border-2 border-green-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-green-700 mb-3">💳 Tax Benefits</h4>
                    <p className="text-gray-700">Up to ₹3.5L tax deduction (80C + 24b)</p>
                  </div>
                </div>

                {/* Eligibility */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h4 className="font-bold text-lg mb-4">📋 Eligibility Criteria</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Age: 21-65 years</li>
                    <li>• Minimum monthly income: ₹25,000 (Salaried) / ₹3L annual (Self-employed)</li>
                    <li>• Good credit score (Above 700 preferred)</li>
                    <li>• Stable employment/business history</li>
                    <li>• Property documents & valuation report</li>
                  </ul>
                </div>

                {/* Benefits */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h4 className="font-bold text-lg mb-4">✅ Key Benefits</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Significant tax savings under Section 80C and 24(b)</li>
                    <li>• Long repayment tenure reduces EMI burden</li>
                    <li>• Property appreciation builds wealth over time</li>
                    <li>• Lower interest rates compared to other loans</li>
                  </ul>
                </div>

                {/* Home Loan EMI Calculator */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-8">
                  <h4 className="font-bold text-2xl text-green-800 mb-6 text-center">🧮 Home Loan EMI Calculator</h4>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Inputs */}
                    <div className="space-y-8">
                      {/* Loan Amount */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-3 block">Loan Amount (₹)</label>
                        <input
                          type="number"
                          min="500000"
                          max="50000000"
                          step="100000"
                          value={loanAmount}
                          onChange={(e) => setLoanAmount(Number(e.target.value) || 0)}
                          className="w-full px-4 py-3 text-lg font-bold text-green-700 border-2 border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 mb-3"
                          placeholder="Enter loan amount"
                        />
                        <input
                          type="range"
                          min="500000"
                          max="50000000"
                          step="100000"
                          value={loanAmount}
                          onChange={(e) => setLoanAmount(Number(e.target.value))}
                          className="w-full h-3 bg-green-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                          <span>₹5L</span>
                          <span>₹5Cr</span>
                        </div>
                        {/* Quick Amount Buttons */}
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <button onClick={() => setLoanAmount(2500000)} className="px-3 py-1 text-xs bg-white border border-green-300 rounded hover:bg-green-100 transition-colors">₹25L</button>
                          <button onClick={() => setLoanAmount(5000000)} className="px-3 py-1 text-xs bg-white border border-green-300 rounded hover:bg-green-100 transition-colors">₹50L</button>
                          <button onClick={() => setLoanAmount(10000000)} className="px-3 py-1 text-xs bg-white border border-green-300 rounded hover:bg-green-100 transition-colors">₹1Cr</button>
                          <button onClick={() => setLoanAmount(20000000)} className="px-3 py-1 text-xs bg-white border border-green-300 rounded hover:bg-green-100 transition-colors">₹2Cr</button>
                        </div>
                      </div>

                      {/* Interest Rate */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-3 block">Interest Rate (% per annum)</label>
                        <input
                          type="number"
                          min="6"
                          max="15"
                          step="0.1"
                          value={interestRate}
                          onChange={(e) => setInterestRate(Number(e.target.value) || 0)}
                          className="w-full px-4 py-3 text-lg font-bold text-green-700 border-2 border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 mb-3"
                          placeholder="Enter interest rate"
                        />
                        <input
                          type="range"
                          min="6"
                          max="15"
                          step="0.1"
                          value={interestRate}
                          onChange={(e) => setInterestRate(Number(e.target.value))}
                          className="w-full h-3 bg-green-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                          <span>6%</span>
                          <span>15%</span>
                        </div>
                      </div>

                      {/* Loan Tenure */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-3 block">Loan Tenure (Years)</label>
                        <input
                          type="number"
                          min="5"
                          max="30"
                          step="1"
                          value={loanTenure}
                          onChange={(e) => setLoanTenure(Number(e.target.value) || 0)}
                          className="w-full px-4 py-3 text-lg font-bold text-green-700 border-2 border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 mb-3"
                          placeholder="Enter tenure"
                        />
                        <input
                          type="range"
                          min="5"
                          max="30"
                          step="1"
                          value={loanTenure}
                          onChange={(e) => setLoanTenure(Number(e.target.value))}
                          className="w-full h-3 bg-green-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                          <span>5 Years</span>
                          <span>30 Years</span>
                        </div>
                        {/* Quick Tenure Buttons */}
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => setLoanTenure(10)} className="px-3 py-1 text-xs bg-white border border-green-300 rounded hover:bg-green-100 transition-colors">10Y</button>
                          <button onClick={() => setLoanTenure(15)} className="px-3 py-1 text-xs bg-white border border-green-300 rounded hover:bg-green-100 transition-colors">15Y</button>
                          <button onClick={() => setLoanTenure(20)} className="px-3 py-1 text-xs bg-white border border-green-300 rounded hover:bg-green-100 transition-colors">20Y</button>
                          <button onClick={() => setLoanTenure(25)} className="px-3 py-1 text-xs bg-white border border-green-300 rounded hover:bg-green-100 transition-colors">25Y</button>
                          <button onClick={() => setLoanTenure(30)} className="px-3 py-1 text-xs bg-white border border-green-300 rounded hover:bg-green-100 transition-colors">30Y</button>
                        </div>
                      </div>
                    </div>

                    {/* Results */}
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <h5 className="text-lg font-bold text-gray-900 mb-4">Your Monthly EMI</h5>
                      <div className="text-4xl font-bold text-green-600 mb-6">
                        ₹{Math.round(emi).toLocaleString('en-IN')}
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Principal Amount</span>
                          <span className="font-semibold text-gray-900">₹{loanAmount.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Total Interest</span>
                          <span className="font-semibold text-orange-600">₹{Math.round(totalInterest).toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Total Amount</span>
                          <span className="font-semibold text-gray-900">₹{Math.round(totalAmount).toLocaleString('en-IN')}</span>
                        </div>
                      </div>

                      <div className="mt-6 p-4 bg-green-50 rounded-lg">
                        <p className="text-xs text-gray-600 text-center">
                          💡 <strong>Tip:</strong> Choose a shorter tenure to save on interest, or longer tenure for lower EMI
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 3. Loan Against Securities */}
            <section id="loan-against-securities" className="scroll-mt-20">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl p-8 mb-6">
                <h2 className="text-3xl font-bold mb-2">3. Loan Against Securities</h2>
                <p className="text-purple-100 text-lg">Leverage your investment portfolio for instant liquidity</p>
              </div>

              <div className="space-y-6">
                {/* What is it */}
                <div className="bg-purple-50 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">What is Loan Against Securities?</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Loan Against Securities (LAS) allows you to pledge your investment portfolio including stocks, bonds, 
                    mutual funds, insurance policies, and fixed deposits to obtain a loan. This provides quick liquidity 
                    while keeping your investments intact and earning returns.
                  </p>
                </div>

                {/* Key Features */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="border-2 border-purple-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-purple-700 mb-3">📊 Loan Amount</h4>
                    <p className="text-gray-700">Up to 50-85% of securities value (Varies by asset type)</p>
                  </div>
                  <div className="border-2 border-purple-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-purple-700 mb-3">💰 Interest Rate</h4>
                    <p className="text-gray-700">9-13% p.a. (Depends on pledged securities)</p>
                  </div>
                  <div className="border-2 border-purple-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-purple-700 mb-3">⏱️ Processing Time</h4>
                    <p className="text-gray-700">Same day to 48 hours</p>
                  </div>
                  <div className="border-2 border-purple-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-purple-700 mb-3">📋 Collateral</h4>
                    <p className="text-gray-700">Stocks, MF, Bonds, Insurance, FD</p>
                  </div>
                </div>

                {/* Eligible Securities */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h4 className="font-bold text-lg mb-4">📌 Eligible Securities</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <ul className="space-y-2 text-gray-700">
                      <li>• Equity shares (Listed stocks)</li>
                      <li>• Mutual fund units</li>
                      <li>• Corporate/Government bonds</li>
                    </ul>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Insurance policies (ULIPs)</li>
                      <li>• Fixed deposits</li>
                      <li>• National Savings Certificates</li>
                    </ul>
                  </div>
                </div>

                {/* Benefits */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h4 className="font-bold text-lg mb-4">✅ Key Benefits</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li>• No need to liquidate investments</li>
                    <li>• Continue earning dividends and capital appreciation</li>
                    <li>• Lower interest rates than unsecured loans</li>
                    <li>• Flexible repayment options</li>
                    <li>• Quick processing with minimal documentation</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="text-center bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-2xl p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Need Help Choosing the Right Loan?
              </h2>
              <p className="text-indigo-100 mb-6 text-lg">
                Get personalized loan recommendations based on your financial situation and goals
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/#contact"
                  className="inline-block bg-white text-indigo-700 px-8 py-4 rounded-xl hover:bg-gray-50 transition-all shadow-lg font-semibold"
                >
                  Get Free Consultation
                </Link>
                <a
                  href="https://wa.me/919248852187?text=Hi, I want to know about loan options"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 text-white px-8 py-4 rounded-xl hover:bg-green-600 transition-all shadow-lg font-semibold"
                >
                  WhatsApp Now
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
